// Aba "Diagnóstico" — qualidade e custo das conversas por agente/período.
// As métricas vêm do trace já gravado nas mensagens; não há chamada extra de IA.
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Card, Checkbox, Empty, Select, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDateTime } from './utils';
import type { ConversaDiag } from './types';

const { Text } = Typography;
type AgenteDiag = 'dwight' | 'jim';

const ROTULO_AGENTE: Record<AgenteDiag, string> = {
  dwight: 'Dwight', jim: 'Jim',
};
const ROTULO_TAREFA: Record<string, string> = {
  proposta: 'Proposta', composicao: 'Composição', licitacao: 'Licitação',
  crm: 'CRM', documento: 'Documento', operacional: 'Operacional',
  estoque: 'Estoque', consulta: 'Consulta', conversa: 'Conversa',
};
const COR_TAREFA: Record<string, string> = {
  proposta: 'blue', composicao: 'purple', licitacao: 'gold', crm: 'cyan',
  documento: 'geekblue', operacional: 'green', estoque: 'lime',
};

function numero(v: unknown) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function modeloCurto(id: string | null) {
  const s = String(id || '').toLowerCase();
  if (!s) return '—';
  if (s.includes('sonnet-4-5')) return 'Sonnet 4.5';
  if (s.includes('haiku-4-5')) return 'Haiku 4.5';
  if (s.includes('gpt-5-6-sol') || s.includes('gpt-5.6-sol')) return 'Sol';
  if (s.includes('gpt-6-astra')) return 'Astra';
  if (s.includes('nova-lite')) return 'Nova Lite';
  return id!.split('/').pop()!.slice(0, 24);
}

function valoresUnicos(valores: Array<string | null | undefined>) {
  return Array.from(new Set(valores.filter((valor): valor is string => Boolean(valor))));
}

function formatarTempo(ms: unknown) {
  const n = numero(ms);
  if (!n) return '—';
  if (n < 60_000) return `${Math.round(n / 1000)}s`;
  if (n < 3_600_000) return `${Math.round(n / 60_000)}min`;
  if (n < 86_400_000) return `${(n / 3_600_000).toFixed(1)}h`;
  return `${(n / 86_400_000).toFixed(1)}d`;
}

export default function DiagnosticoTab() {
  const [convs, setConvs] = useState<ConversaDiag[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroProblema, setFiltroProblema] = useState(false);
  const [agente, setAgente] = useState<AgenteDiag>('dwight');
  const [periodoDias, setPeriodoDias] = useState(30);
  const sequenciaRequisicao = useRef(0);

  useEffect(() => {
    const requisicaoAtual = ++sequenciaRequisicao.current;
    let ativa = true;

    setLoading(true);
    setErro(null);

    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/agentes/exemplos`,
          { action: 'diagnostico', agente, periodo_dias: periodoDias },
          { headers: { Authorization: token } });
        if (!ativa || requisicaoAtual !== sequenciaRequisicao.current) return;
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setConvs(Array.isArray(data) ? data : []);
      } catch {
        if (!ativa || requisicaoAtual !== sequenciaRequisicao.current) return;
        setErro('Não foi possível carregar o diagnóstico. Tente novamente em alguns instantes.');
        setConvs([]);
      } finally {
        if (ativa && requisicaoAtual === sequenciaRequisicao.current) setLoading(false);
      }
    })();

    return () => {
      ativa = false;
      if (sequenciaRequisicao.current === requisicaoAtual) sequenciaRequisicao.current += 1;
    };
  }, [agente, periodoDias]);

  const filtradas = filtroProblema
    ? convs.filter(c => numero(c.user_sem_resposta) > 0 || numero(c.erros_tools) > 0
      || numero(c.n_truncamentos) > 0 || numero(c.cards_com_erro) > 0)
    : convs;

  const resumo = useMemo(() => convs.reduce((acc, c) => ({
    tokens: acc.tokens + numero(c.tokens_totais),
    custo: acc.custo + numero(c.custo_estimado_usd),
    cards: acc.cards + numero(c.cards_total),
    aplicados: acc.aplicados + numero(c.cards_aplicados),
    cortes: acc.cortes + numero(c.n_truncamentos),
  }), { tokens: 0, custo: 0, cards: 0, aplicados: 0, cortes: 0 }), [convs]);

  const cols: ColumnsType<ConversaDiag> = [
    { title: 'Conversa', dataIndex: 'titulo', key: 't', width: 240, render: (v) => v || '(sem título)' },
    {
      title: 'Último tipo', dataIndex: 'tipo_tarefa', key: 'tipo', width: 105,
      render: (v) => v ? <Tag color={COR_TAREFA[v]}>{ROTULO_TAREFA[v] || v}</Tag> : <Text type="secondary">histórico</Text>,
    },
    {
      title: 'Modelos', key: 'modelo', width: 125,
      render: (_, c) => {
        const modelos = valoresUnicos(c.modelos_ids || []);
        const assinaturas = valoresUnicos([
          ...(c.prompt_efetivo_fingerprints || []),
          c.prompt_efetivo_fingerprint,
        ]);
        const rotulo = modelos.length > 1
          ? `Misto (${modelos.length})`
          : modelos.length === 1 ? modeloCurto(modelos[0]) : 'histórico';
        return (
          <Tooltip title={(
            <div>
              <div>Modelos: {modelos.length ? modelos.join(', ') : 'sem registro'}</div>
              <div>Prompts efetivos: {assinaturas.length ? assinaturas.join(', ') : 'sem assinatura registrada'}</div>
            </div>
          )}>
            <Text type={modelos.length ? undefined : 'secondary'}>{rotulo}</Text>
          </Tooltip>
        );
      },
    },
    { title: 'Mensagens', dataIndex: 'n_mensagens', key: 'nm', align: 'center', width: 90 },
    { title: 'Pessoa/IA', key: 'ua', align: 'center', width: 90, render: (_, c) => `${c.n_user}/${c.n_assist}` },
    {
      title: 'Pior tempo', dataIndex: 'max_duracao_ms', key: 'mt', align: 'right', width: 95,
      render: (v) => v ? `${(numero(v) / 1000).toFixed(1)}s` : '—',
    },
    {
      title: 'Tokens', dataIndex: 'tokens_totais', key: 'tt', align: 'right', width: 100,
      render: (v) => numero(v) ? numero(v).toLocaleString('pt-BR') : '—',
    },
    {
      title: 'Custo est.', dataIndex: 'custo_estimado_usd', key: 'cu', align: 'right', width: 105,
      render: (v) => numero(v) ? `US$ ${numero(v).toFixed(4)}` : '—',
    },
    {
      title: 'Cards', key: 'cards', align: 'center', width: 100,
      render: (_, c) => numero(c.cards_total) ? (
        <Tooltip title={`${numero(c.cards_aplicados)} aplicados · ${numero(c.cards_pendentes)} pendentes · ${numero(c.cards_descartados)} descartados · ${numero(c.cards_com_erro)} com erro`}>
          <Text type={numero(c.cards_com_erro) ? 'danger' : undefined}>{numero(c.cards_aplicados)}/{numero(c.cards_total)}</Text>
        </Tooltip>
      ) : '—',
    },
    {
      title: 'Até aplicar', dataIndex: 'tempo_aplicacao_mediana_ms', key: 'ta', align: 'right', width: 95,
      render: formatarTempo,
    },
    {
      title: 'Cortes', dataIndex: 'n_truncamentos', key: 'tr', align: 'center', width: 70,
      render: (v) => <Text strong={numero(v) > 0} type={numero(v) > 0 ? 'danger' : 'secondary'}>{numero(v)}</Text>,
    },
    {
      title: 'Órfãs', dataIndex: 'user_sem_resposta', key: 'or', align: 'center', width: 70,
      render: (v) => <Text strong={numero(v) > 0} type={numero(v) > 0 ? 'danger' : 'secondary'}>{numero(v)}</Text>,
    },
    {
      title: 'Erros', dataIndex: 'erros_tools', key: 'et', align: 'center', width: 65,
      render: (v) => <Text strong={numero(v) > 0} type={numero(v) > 0 ? 'danger' : 'secondary'}>{numero(v)}</Text>,
    },
    {
      title: 'Atualizado', dataIndex: 'atualizado_em', key: 'at', width: 140,
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDateTime(v)}</Text>,
    },
  ];

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 14 }}
        message={`Qualidade e custo do ${ROTULO_AGENTE[agente]}`}
        description="Os totais abrangem todos os ciclos e eventos registrados no período. O custo é estimado; conversas com mais de um modelo aparecem como mistura, e o detalhe lista os modelos e as assinaturas efetivas de prompt." />

      <Space wrap style={{ marginBottom: 14 }}>
        <Text>Agente:</Text>
        <Select<AgenteDiag> value={agente} onChange={setAgente} style={{ width: 120 }} options={[
          { value: 'dwight', label: 'Dwight' }, { value: 'jim', label: 'Jim' },
        ]} />
        <Text>Período:</Text>
        <Select value={periodoDias} onChange={setPeriodoDias} style={{ width: 110 }} options={[
          { value: 7, label: '7 dias' }, { value: 30, label: '30 dias' }, { value: 90, label: '90 dias' },
        ]} />
        <Checkbox checked={filtroProblema} onChange={(e) => setFiltroProblema(e.target.checked)}>
          Só com problemas
        </Checkbox>
      </Space>

      {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 14 }} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        <Card size="small"><Statistic title="Conversas" value={convs.length} /></Card>
        <Card size="small"><Statistic title="Tokens" value={resumo.tokens} groupSeparator="." /></Card>
        <Card size="small"><Statistic title="Custo estimado" value={resumo.custo} precision={4} prefix="US$" /></Card>
        <Card size="small"><Statistic title="Cards aplicados" value={resumo.aplicados} suffix={resumo.cards ? `/ ${resumo.cards}` : ''} /></Card>
        <Card size="small"><Statistic title="Cortes" value={resumo.cortes} valueStyle={resumo.cortes ? { color: '#B91626' } : undefined} /></Card>
      </div>

      <Table
        rowKey="id" columns={cols} dataSource={filtradas} loading={loading}
        size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1500 }}
        rowClassName={(c) => (numero(c.user_sem_resposta) > 0 || numero(c.erros_tools) > 0
          || numero(c.n_truncamentos) > 0 || numero(c.cards_com_erro) > 0) ? 'row-pendente' : ''}
        locale={{ emptyText: <Empty description={filtroProblema ? 'Nenhuma conversa com problemas.' : `Nenhuma conversa do ${ROTULO_AGENTE[agente]} no período.`} /> }}
      />
    </div>
  );
}
